import {
	ActionButton,
	Menu,
	MenuItem,
	MenuTrigger,
	Popover,
	Text,
} from '@react-spectrum/s2';
import AlertTriangle from '@react-spectrum/s2/icons/AlertTriangle';
import More from '@react-spectrum/s2/icons/More';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { useUserId } from '@/auth/UserIdContext';
import { BlobImage } from '@/components/blob/BlobImage';
import { logger } from '@/logger';
import { dismissDeletedArtifact } from '@/spaces/content/contentDetailUtils';
import { DeleteConfirmationWrapper } from '@/spaces/content/DeleteConfirmationWrapper';
import { useArtifactEditChat } from '@/spaces/content/useArtifactEditChat';
import { useDownloadAd } from '@/spaces/content/useDownloadAd';
import { trackEvent } from '@/utils/fullstory';

import type { DigitalAd } from '../../../../bridge/types/artifact.ts';

interface DigitalAdContentDetailProps {
	artifact: DigitalAd;
	refresh?: () => Promise<void>;
}

export const DigitalAdContentDetail = ({
	artifact,
	refresh,
}: DigitalAdContentDetailProps) => {
	const bridgeApi = useBridgeApi();
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [mutationError, setMutationError] = useState<string | null>(null);
	const {
		download: handleDownload,
		isDownloading,
		error: downloadError,
	} = useDownloadAd(artifact);

	const images = artifact.content.image_urls ?? [];
	const firstImage = images[0];

	const handleDelete = async () => {
		if (!userId) return;
		setIsDeleting(true);
		setMutationError(null);
		try {
			await bridgewayApi.patch(`/api/user/${userId}/artifacts/${artifact.id}`, {
				status: 'cancelled',
			});
			trackEvent('Digital Ad Deleted', { artifact_id: artifact.id });
			setShowDeleteConfirmation(false);
			await refresh?.();
			dismissDeletedArtifact(bridgeApi, artifact.id);
		} catch (error) {
			setMutationError('Failed to delete ad. Please try again.');
			logger.error('Failed to delete digital ad', error);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleEditAd = useArtifactEditChat(artifact);

	const headline = artifact.content.headline || 'Untitled ad';
	const visibleError = mutationError ?? downloadError;

	return (
		<div className="flex h-full min-h-0 flex-col gap-6">
			<div className="flex items-center justify-end gap-4">
				{visibleError && (
					<span className="flex items-center gap-2">
						<AlertTriangle />
						<Text
							styles={style({
								font: 'body',
								fontSize: 'body',
								color: 'body',
							})}
						>
							{visibleError}
						</Text>
					</span>
				)}
				<MenuTrigger>
					<DeleteConfirmationWrapper
						onConfirm={handleDelete}
						onCancel={() => setShowDeleteConfirmation(false)}
						isOpen={showDeleteConfirmation}
						title="Delete digital ad?"
						body="This ad will be removed from your Content Library."
					>
						<ActionButton isQuiet aria-label="More options">
							<More />
						</ActionButton>
					</DeleteConfirmationWrapper>
					<Popover>
						<Menu>
							<MenuItem id="edit" onPress={handleEditAd}>
								<Text>Edit</Text>
							</MenuItem>
							<MenuItem
								id="download"
								onPress={handleDownload}
								isDisabled={isDownloading || !firstImage}
							>
								<Text>Download</Text>
							</MenuItem>
							<MenuItem
								id="delete"
								onPress={() => setShowDeleteConfirmation(true)}
								isDisabled={isDeleting}
							>
								<Text>Delete</Text>
							</MenuItem>
						</Menu>
					</Popover>
				</MenuTrigger>
			</div>

			<div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
				<div className="flex items-center justify-center">
					<BlobImage
						src={firstImage}
						alt={`Digital ad: ${headline}`}
						className="max-h-[60vh] w-auto max-w-full rounded-lg object-contain"
					/>
				</div>
			</div>
		</div>
	);
};
